import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  CopyButton,
  Group,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useController, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IconAlertCircle,
  IconBuildingStore,
  IconCheck,
  IconCopy,
  IconDeviceTv,
  IconEye,
  IconPhoto,
  IconRocket,
  IconSettings,
} from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import { APP_STATE_TYPE_ID } from '@/lib/constants/app';
import { FACTORY_STORE_FIRST_SUBMISSION, FACTORY_STORE_INFO } from '@/lib/constants/appFactory';
import {
  APP_PLATFORM_CONSTRAINTS,
  APP_PLATFORM_INFO,
  type AppPlatformInfo,
  type AppPlatformKey,
} from '@/lib/constants/appPlatform';
import type { FactoryStoreKey } from '@/lib/constants/appFactory';
import type { AppCfgPlatformRow, AppCfgsDto } from '@/lib/types/App';
import { ApiError } from '@/shared/api/ApiError';
import { FormSaveBar } from '@/shared/components/FormSaveBar';
import { AppPlatformUtils } from '../AppPlatformUtils';
import { StoreAccountUtils } from '../StoreAccountUtils';
import {
  appPlatformEnableFormSchema,
  appPlatformFormSchema,
  type AppPlatformEnableFormSchemaData,
  type AppPlatformFormSchemaData,
} from '../schemas/AppPlatformForm.schema';
import { useFactorySubmissions, useIsFactoryOperator, useStoreAccounts } from '../useAppFactory';
import { useCreateAppPlatform, useUpdateAppPlatform } from '../useAppMutations';
import { AppImagesPanel } from './AppImagesPanel';
import { AppPlatformStatusTab } from './AppPlatformStatusTab';
import { AppRokuSetupWizard, type WizardStep } from './AppRokuSetupWizard';
import { AppTvPreview } from './AppTvPreview';

interface AppPlatformTabProps {
  appCfgId: number;
  app: AppCfgsDto;
  isAppActive: boolean;
  /** Which generic platform this tab manages (Fire TV, Google TV). */
  platform: AppPlatformKey;
}

/**
 * One generic app platform (an app_cfg_platform row): the enable card until
 * the row exists, then Settings | Images | Status | Preview. Images (3.34.0)
 * is the platform's factory deliverable set — the brand files a build is made
 * with and the store-listing art — and appears only for a platform that has one
 * (`assetPlatform`; Google TV shares Fire TV's set); Preview is the shared TV
 * home-screen preview asking for this platform's cfg. The parent renders this
 * tab only for an account entitled to the platform — without the entitlement it
 * is absent, not disabled.
 */
export function AppPlatformTab({ appCfgId, app, isAppActive, platform }: AppPlatformTabProps) {
  const info = APP_PLATFORM_INFO[platform];
  const row = AppPlatformUtils.rowFor(app, info.typeId);
  // The store-onboarding steps (address, listing, privacy policy, store account).
  // null = closed; no `step` = the wizard's own first unsatisfied step (the address
  // flow: a pinned 'domain' would snap back to it when the new address refetches).
  const [wizardAt, setWizardAt] = useState<{ step?: WizardStep } | null>(null);
  const openWizardAt = useCallback((step: WizardStep) => setWizardAt({ step }), []);
  const stores = useMemo(() => [info.store], [info.store]);
  // Controlled: the Status tab's first-submission card leads to the store app id
  // field, and the Readiness card's fix buttons to the Images / Settings sub-tabs.
  const [section, setSection] = useState<string>('settings');
  // A store that knows the app by its package name has no store app id to gate
  // the first-submission card on: the store's own submissions do (the same read
  // the Status tab makes; asked only for such a platform with a row).
  const submissionsQuery = useFactorySubmissions(appCfgId, !!row && !!info.storeIdentityIsPackage);
  const firstSubmissionDone = info.storeIdentityIsPackage
    ? StoreAccountUtils.firstSubmissionDone(submissionsQuery.data?.submissions ?? [], info.store)
    : !!row?.storeAppId;

  const wizard = (
    <AppRokuSetupWizard
      opened={wizardAt != null}
      app={app}
      initialStep={wizardAt?.step}
      storeSetupOnly
      stores={stores}
      onClose={() => setWizardAt(null)}
      onFinished={() => setWizardAt(null)}
    />
  );

  if (!row) {
    return (
      <>
        <PlatformEnableCard
          appCfgId={appCfgId}
          app={app}
          info={info}
          isAppActive={isAppActive}
          onSetUpAddress={() => setWizardAt({})}
        />
        {wizard}
      </>
    );
  }

  return (
    <Tabs value={section} onChange={(v) => setSection(v ?? 'settings')} variant="pills">
      <Tabs.List mb="md">
        <Tabs.Tab value="settings" leftSection={<IconSettings size={14} />}>
          Settings
        </Tabs.Tab>
        {info.assetPlatform && (
          <Tabs.Tab value="images" leftSection={<IconPhoto size={14} />}>
            Images
          </Tabs.Tab>
        )}
        <Tabs.Tab value="status" leftSection={<IconRocket size={14} />}>
          Status
        </Tabs.Tab>
        <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>
          Preview
        </Tabs.Tab>
        <Button
          type="button"
          size="xs"
          variant="light"
          ml="auto"
          leftSection={<IconBuildingStore size={14} />}
          // no Store Listing sub-tab here: the wizard's listing step is where those details live
          onClick={() => openWizardAt('listing')}
          disabled={!isAppActive}
        >
          Store Setup
        </Button>
      </Tabs.List>

      <Tabs.Panel value="settings" keepMounted>
        <PlatformSettingsForm appCfgId={appCfgId} info={info} row={row} isAppActive={isAppActive} />
      </Tabs.Panel>

      {info.assetPlatform && (
        <Tabs.Panel value="images">
          {/* One writer of the deliverable store (ADR-0017): generate (Brand Kit) or
              manual per-slot uploads, and only Approve & Publish goes live — the same
              surface as the Roku tab, on this platform's set. */}
          <AppImagesPanel appCfgId={appCfgId} platform={info.assetPlatform} disabled={!isAppActive} appTitle={row.title ?? undefined} />
        </Tabs.Panel>
      )}

      <Tabs.Panel value="status">
        <AppPlatformStatusTab
          appCfgId={appCfgId}
          app={app}
          platform={info.key}
          store={info.store}
          readinessStores={stores}
          onOpenWizard={openWizardAt}
          onOpenSection={setSection}
          // no step: the wizard opens at its first unsatisfied one (the address flow)
          onSetUpAddress={() => setWizardAt({})}
          isAppActive={isAppActive}
          intro={
            firstSubmissionDone ? null : (
              <FirstSubmissionCard info={info} onEnterAppId={() => setSection('settings')} />
            )
          }
        />
      </Tabs.Panel>

      <Tabs.Panel value="preview">
        <AppTvPreview app={app} platform={info.key} />
      </Tabs.Panel>

      {wizard}
    </Tabs>
  );
}

// ── Internal: enable card (no row yet) ──

interface PlatformEnableCardProps {
  appCfgId: number;
  app: AppCfgsDto;
  info: AppPlatformInfo;
  isAppActive: boolean;
  onSetUpAddress: () => void;
}

function PlatformEnableCard({ appCfgId, app, info, isAppActive, onSetUpAddress }: PlatformEnableCardProps) {
  const createMutation = useCreateAppPlatform(appCfgId, info.key);
  // The app's address is its tenancy anchor: builds fail closed without it.
  const hasAddress = app.web != null;

  const titleLimit = useMemo(() => AppPlatformUtils.titleLimit(info), [info]);
  const enableSchema = useMemo(() => appPlatformEnableFormSchema(titleLimit), [titleLimit]);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AppPlatformEnableFormSchemaData>({
    resolver: zodResolver(enableSchema),
    defaultValues: { title: app.appCfg.orgInfo.name.slice(0, APP_PLATFORM_CONSTRAINTS.TITLE_MAX) },
  });
  const { field: titleField } = useController({ control, name: 'title' });

  const handleEnable = handleSubmit((data) => {
    // Enter in the field submits too: one enable at a time (a second one answers 409).
    if (createMutation.isPending) return;
    createMutation.mutate(AppPlatformUtils.buildCreateReq(appCfgId, data.title), {
      onSuccess: () =>
        notifications.show({
          title: `${info.label} App Enabled`,
          message: `The ${info.label} platform has been enabled.`,
          color: 'green',
        }),
      onError: (err) => notifications.show({ title: 'Error', message: err.message, color: 'red' }),
    });
  });

  const labelLong = info.labelLong ?? info.label;

  return (
    <Card withBorder p="xl" radius="md">
      <form onSubmit={handleEnable}>
        <Stack align="center" gap="md">
          <IconDeviceTv size={48} color="var(--mantine-color-orange-5)" />
          <Text fw={600} size="lg">
            {labelLong} App
          </Text>
          <Text c="dimmed" ta="center" maw={420}>
            Bring your content to {labelLong} devices. Enable the {info.appNoun} to reach your
            audience on their TV.
          </Text>
          {!isAppActive ? (
            <Text size="sm" c="orange">
              Activate the app first to enable the {info.label} platform.
            </Text>
          ) : !hasAddress ? (
            <>
              <Text size="sm" c="dimmed" ta="center" maw={420}>
                Your app needs its address first — the {info.appNoun} loads its content from it.
              </Text>
              <Button type="button" onClick={onSetUpAddress}>
                Set Up App Address
              </Button>
            </>
          ) : (
            <>
              <TextInput
                label="App Name"
                description={AppPlatformUtils.titleDescription(info, `Shown on the ${info.label} home screen.`, titleField.value)}
                required
                w="100%"
                maw={420}
                maxLength={titleLimit.max}
                value={titleField.value}
                onChange={titleField.onChange}
                onBlur={titleField.onBlur}
                error={errors.title?.message}
              />
              <Text size="xs" c="dimmed" ta="center" maw={420}>
                The app's {info.storePackageIdLabel} is assigned automatically when you enable it.
              </Text>
              <Button type="submit" loading={createMutation.isPending}>
                Enable {info.label} App
              </Button>
            </>
          )}
        </Stack>
      </form>
    </Card>
  );
}

// ── Internal: settings form ──

interface PlatformSettingsFormProps {
  appCfgId: number;
  info: AppPlatformInfo;
  row: AppCfgPlatformRow;
  isAppActive: boolean;
}

function PlatformSettingsForm({ appCfgId, info, row, isAppActive }: PlatformSettingsFormProps) {
  const updateMutation = useUpdateAppPlatform(appCfgId, info.key);
  // The API assigns the package; the holder side sets the store app id once; every
  // other store-field change is an operator's (AppPlatformUtils, AppCfgPlatformRules).
  const isOperator = useIsFactoryOperator();
  const { supportContact, operatorName } = useHelpTokens();
  const { account } = usePlatformStoreAccount(info.store);
  const operatorRecordsAppId = AppPlatformUtils.operatorRecordsStoreAppId(isOperator, account);
  const disabled = !isAppActive;
  const storedPackageId = row.storePackageId ?? '';
  const storedAppId = row.storeAppId ?? '';
  const canOverridePackage = AppPlatformUtils.canOverridePackage(row, isOperator);
  // The Override is a deliberate act: the editable package stays folded away until asked for.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const schema = useMemo(
    () =>
      appPlatformFormSchema({
        packageKind: info.packageKind,
        storeAppIdPattern: info.storeAppIdPattern,
        storeAppIdPatternMessage: info.storeAppIdPatternMessage,
        isOperator,
        stored: { storePackageId: storedPackageId, storeAppId: storedAppId },
        titleLimit: AppPlatformUtils.titleLimit(info),
      }),
    [info, isOperator, storedPackageId, storedAppId],
  );

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    setError,
    watch,
    formState: { isDirty, errors },
  } = useForm<AppPlatformFormSchemaData>({
    resolver: zodResolver(schema),
    defaultValues: AppPlatformUtils.transformRowToFormData(row),
  });

  // Sync form when the row changes (after a save's refetch)
  useEffect(() => {
    reset(AppPlatformUtils.transformRowToFormData(row));
    setOverrideOpen(false);
  }, [row, reset]);

  const { field: stateField } = useController({ control, name: 'appStateTypeId' });
  const { field: titleField } = useController({ control, name: 'title' });
  const { field: packageField } = useController({ control, name: 'storePackageId' });
  const { field: appIdField } = useController({ control, name: 'storeAppId' });
  const isPlatformActive = row.appStateTypeId === APP_STATE_TYPE_ID.Active;
  const appIdState = AppPlatformUtils.storeAppIdState(row, isOperator, watch('storePackageId'));

  const handleSave = handleSubmit((data) => {
    if (updateMutation.isPending) return;
    const form = { ...data, appStateTypeId: data.appStateTypeId as AppCfgPlatformRow['appStateTypeId'] };
    const req = AppPlatformUtils.transformFormToUpdateReq(form, row, isOperator);
    updateMutation.mutate(req, {
      onSuccess: () => {
        // Clean baseline immediately — the refetch-driven effect reset arrives later.
        reset(data);
        setOverrideOpen(false);
        notifications.show({
          title: 'Saved',
          message: `${info.label} settings updated successfully.`,
          color: 'green',
        });
      },
      onError: (err) => {
        // A refused store field is shown on that field, in the API's own sentence.
        const status = err instanceof ApiError && !err.isNotAuthenticated ? err.httpStatusCode : 0;
        const field = AppPlatformUtils.refusalField(status, req, err.message);
        if (!field) {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
          return;
        }
        if (field === 'storePackageId') setOverrideOpen(true);
        setError(field, {
          message: AppPlatformUtils.refusalMessage(
            status,
            field,
            isOperator,
            err.message,
            info.storeAppIdLabel,
            supportContact,
          ),
        });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    reset(AppPlatformUtils.transformRowToFormData(row));
    setOverrideOpen(false);
  }, [row, reset]);

  const closeOverride = useCallback(() => {
    resetField('storePackageId');
    setOverrideOpen(false);
  }, [resetField]);

  return (
    <form onSubmit={handleSave}>
      <FormSaveBar
        isDirty={isDirty}
        isPending={updateMutation.isPending}
        onDiscard={handleDiscard}
        saveDisabled={disabled}
      />

      {disabled && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" mt="md">
          {info.label} settings are read-only while the parent app is inactive. Activate the app to
          make changes.
        </Alert>
      )}

      <Stack gap="lg" mt="md">
        <Switch
          label={`${info.label} App Active`}
          description={
            isPlatformActive
              ? `${info.label} platform is enabled for this app`
              : `${info.label} platform is disabled for this app`
          }
          checked={stateField.value === APP_STATE_TYPE_ID.Active}
          onChange={(e) =>
            stateField.onChange(
              e.currentTarget.checked ? APP_STATE_TYPE_ID.Active : APP_STATE_TYPE_ID.Inactive,
            )
          }
          disabled={disabled}
        />

        <TextInput
          label="App Name"
          description={AppPlatformUtils.titleDescription(
            info,
            `Shown on the ${info.label} home screen. A change reaches devices with the next app version.`,
            titleField.value,
          )}
          value={titleField.value}
          onChange={titleField.onChange}
          onBlur={titleField.onBlur}
          maxLength={AppPlatformUtils.titleLimit(info).max}
          error={errors.title?.message}
          disabled={disabled}
        />

        <Text fw={600} size="md">
          Store identity
        </Text>

        {overrideOpen && canOverridePackage ? (
          <TextInput
            label={info.storePackageIdLabel}
            placeholder="com.example.tv"
            description={
              storedPackageId && packageField.value.trim() === ''
                ? 'Empty: saving returns the app to its automatically assigned package name.'
                : 'Only for an app that already exists in a store under another package name — an update in place needs the same one. Permanent once the app exists in the store.'
            }
            value={packageField.value}
            onChange={packageField.onChange}
            onBlur={packageField.onBlur}
            maxLength={APP_PLATFORM_CONSTRAINTS.STORE_PACKAGE_ID_MAX}
            error={errors.storePackageId?.message}
            disabled={disabled}
            inputContainer={(children) => (
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1 }}>{children}</div>
                {storedPackageId && (
                  <Button
                    type="button"
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => packageField.onChange('')}
                    disabled={disabled}
                  >
                    Use the assigned package
                  </Button>
                )}
                <Button type="button" variant="subtle" size="compact-sm" onClick={closeOverride}>
                  Cancel
                </Button>
              </Group>
            )}
          />
        ) : (
          <Stack gap={4}>
            {storedPackageId ? (
              <ReadOnlyIdField
                label={info.storePackageIdLabel}
                description="Assigned automatically. The store knows your app by this name, so it does not change."
                value={storedPackageId}
              />
            ) : (
              <div>
                <Text size="sm" fw={500}>
                  {info.storePackageIdLabel}
                </Text>
                <Text size="sm" c="dimmed">
                  Not assigned yet — contact {supportContact} to have it assigned.
                </Text>
              </div>
            )}
            {canOverridePackage && (
              <Group>
                <Button
                  type="button"
                  variant="subtle"
                  size="compact-xs"
                  onClick={() => setOverrideOpen(true)}
                  disabled={disabled}
                >
                  {storedPackageId ? 'Override package (operator)' : 'Assign package (operator)'}
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {info.storeIdentityIsPackage ? (
          <Text size="sm" c="dimmed">
            {FACTORY_STORE_INFO[info.store].label} knows the app by this package name; there is no
            separate store app id.
          </Text>
        ) : appIdState === 'readonly' ? (
          <ReadOnlyIdField
            label={info.storeAppIdLabel}
            description={`Recorded. Contact ${supportContact} to change it.`}
            value={storedAppId}
          />
        ) : (
          <TextInput
            label={info.storeAppIdLabel}
            description={
              appIdState === 'needs-package'
                ? `Available once the app has its ${info.storePackageIdLabel}.`
                : operatorRecordsAppId
                  ? `${operatorName} creates the app in the store's developer console and records this for you — nothing to do.`
                  : `${info.storeAppIdStep} ${info.storeAppIdHint}${
                      isOperator ? '' : ` It is set once — contact ${supportContact} to change it later.`
                    }`
            }
            value={appIdField.value}
            onChange={(e) =>
              appIdField.onChange(AppPlatformUtils.cleanStoreAppIdInput(e.currentTarget.value))
            }
            onBlur={appIdField.onBlur}
            maxLength={APP_PLATFORM_CONSTRAINTS.STORE_APP_ID_MAX}
            error={errors.storeAppId?.message}
            disabled={disabled || appIdState === 'needs-package'}
          />
        )}
      </Stack>
    </form>
  );
}

// ── Internal: a recorded store id — read-only, with the dashboard's copy affordance ──

function ReadOnlyIdField({ label, description, value }: { label: string; description: string; value: string }) {
  return (
    <TextInput
      label={label}
      description={description}
      value={value}
      readOnly
      styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
      rightSectionWidth={36}
      rightSection={
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="left">
              <ActionIcon
                type="button"
                variant="subtle"
                color={copied ? 'teal' : 'gray'}
                onClick={copy}
                size="sm"
                aria-label={`Copy ${label}`}
              >
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}

// ── Internal: the store account this app publishes from (its own, else a covering parent's) ──

function usePlatformStoreAccount(store: FactoryStoreKey) {
  const { selectedCId, selectedCustomer } = useAuth();
  const storeAccountsQuery = useStoreAccounts();
  const parentCIds = selectedCustomer?.cfg.parentCId ? [selectedCustomer.cfg.parentCId] : [];
  const account = StoreAccountUtils.resolve(
    storeAccountsQuery.data?.storeAccounts ?? [],
    selectedCId ?? 0,
    parentCIds,
    store,
  );
  // Unknown while the accounts load (or when the read failed).
  return { account, isKnown: storeAccountsQuery.isSuccess };
}

// ── Internal: the store's first-submission guide (until the store app id is recorded,
//    or — for a store that knows the app by its package name — until a submission has
//    gone past Ready to submit) ──

function FirstSubmissionCard({ info, onEnterAppId }: { info: AppPlatformInfo; onEnterAppId: () => void }) {
  const guide = FACTORY_STORE_FIRST_SUBMISSION[info.store];
  const { operatorName } = useHelpTokens();
  const isOperator = useIsFactoryOperator();
  const { account, isKnown } = usePlatformStoreAccount(info.store);
  if (!guide) return null;

  // A store published from the operator's own account: the operator creates the
  // app whatever rows exist, and there is no field to lead anyone to.
  const operatorStore = StoreAccountUtils.isOperatorAccountStore(info.store);
  // Whoever creates the app in the console is led to the field; a delegated holder has nothing to do.
  const entersAppId =
    !info.storeIdentityIsPackage && !operatorStore && (isOperator || (isKnown && account?.submitter === 'holder'));
  // Unknown while the accounts load (or when the read failed): say who only once it is known.
  const who = operatorStore
    ? guide.byOperator.replace('{operator}', operatorName)
    : !isKnown
      ? null
      : !account
        ? guide.noAccount
        : account.state !== 'active'
          ? StoreAccountUtils.whoSubmits(account, operatorName, FACTORY_STORE_INFO[info.store].label)
          : account.submitter === 'holder'
            ? guide.byHolder
            : guide.byOperator.replace('{operator}', operatorName);

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="xs">
        <Text fw={600}>{guide.title}</Text>
        {guide.facts.map((f) => (
          <Text size="sm" key={f}>
            {f}
          </Text>
        ))}
        {who && <Text size="sm">{who}</Text>}
        <Text size="sm" c="dimmed">
          {guide.after}
        </Text>
        {entersAppId && (
          <Group>
            <Button type="button" size="xs" variant="light" onClick={onEnterAppId}>
              Enter the {info.storeAppIdLabel}
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
